/**
 * Groundwork Tasks v1 — inline open-task list for entity pages (spec §Work
 * Inbox: "Job/customer pages show their open tasks inline"). Same rows as the
 * inbox, filtered by link; renders nothing when there are no open tasks.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ListTodo } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ageChip, type Task } from "@/lib/tasks";
import { TaskDrawer } from "@/components/task-drawer";

export function TaskListInline({ entityType, entityId }: { entityType: string; entityId: string }) {
    const [openTaskId, setOpenTaskId] = useState<string | null>(null);

    const tasksQ = useQuery({
        queryKey: ["tasks", "entity", entityType, entityId],
        queryFn: () =>
            api.get<{ tasks: Task[] }>(`/api/tasks?entityType=${entityType}&entityId=${entityId}&status=open`),
        enabled: !!entityId,
    });

    const tasks = tasksQ.data?.tasks ?? [];
    if (tasks.length === 0) return null;

    return (
        <div className="rounded-xl border bg-card overflow-hidden">
            <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
                <ListTodo className="h-4 w-4 text-foreground/70" />
                <span className="text-sm font-semibold">Open tasks ({tasks.length})</span>
            </div>
            <div className="divide-y">
                {tasks.map((t) => {
                    const chip = ageChip(t.createdAt);
                    return (
                        <button
                            key={t.id}
                            onClick={() => setOpenTaskId(t.id)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
                        >
                            {t.priority === "urgent" && (
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                            )}
                            <span className="flex-1 truncate text-sm">{t.title}</span>
                            <span className="shrink-0 text-[11px] text-foreground/60">
                                {t.assigneeName ?? (t.queueName ? `${t.queueName} queue` : "")}
                            </span>
                            {chip && (
                                <span
                                    className={cn(
                                        "shrink-0 rounded-full px-1.5 py-px text-[11px] font-medium",
                                        chip.amber ? "bg-amber-500/15 text-amber-600" : "bg-muted",
                                    )}
                                >
                                    {chip.label}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
        </div>
    );
}
